'use client';

import { RangeSlider } from '@app/app/_components/RangeSlider/RangeSlider';
import { SwitchChoiceButtonInput } from '@app/app/_components/SwitchChoiceButtonInput/SwitchChoiceButtonInput';
import { RangeSliderUnit } from '@app/domain/model/RangeSliderUnit';
import { LifeCover } from '@app/domain/model/eshop/life/LifeCover';
import { useTranslations } from 'next-intl';
import { determineCoverageName } from '@app/infrastructure/policies/determineCoverageName';
import { ArrowRightIcon } from '@app/app/_icons/ArrowRightIcon';
import { SelectedCover } from '@app/domain/model/eshop/life/SelectedCover';
import { CallbackButton } from '@app/app/_components/CallbackButton/CallbackButton';
import { Tag } from '@app/app/_components/Tag/Tag';
import { formatEuroValue } from '@app/infrastructure/application/formatEuroValue';
import { useAppParameters } from '@app/app/_context/AppParametersContext/useAppParameters';

interface Props {
    cover: LifeCover;
    selectedCover?: SelectedCover;
    min: number;
    max: number;
    step: number;
    isSwitchDisabled: boolean;
    canUseChoiceButtons: boolean;
    options: number[];
    onSwitchChange: (coverCode: string, checked: boolean) => void;
    onValueChange: (coverCode: string, value: number) => void;
    onReadMoreClick: (coverCode: string) => void;
}

export function CoverConfigItem({
    cover,
    selectedCover,
    min,
    max,
    step,
    isSwitchDisabled,
    canUseChoiceButtons,
    options,
    onSwitchChange,
    onValueChange,
    onReadMoreClick,
}: Props) {
    const { tenant } = useAppParameters();
    const t = useTranslations();

    const additionalContent = (
        <CallbackButton
            id={cover.code}
            variant="text"
            className="h-min w-max p-0"
            preventDefault
            isLoading={false}
            ignoreFormStatus
            rightAddon={<ArrowRightIcon className="size-4" />}
            onClick={onReadMoreClick}>
            {t('general.readMore')}
        </CallbackButton>
    );

    const footerContent = (
        <Tag variant="suggestion">
            {t('general.recommendedValue', { value: formatEuroValue(cover.default, tenant, true) })}
        </Tag>
    );

    return (
        <div>
            <input
                type="hidden"
                name={`cover_${cover.code}_enabled`}
                value={selectedCover?.enabled ? 'true' : 'false'}
            />
            <input type="hidden" name={`cover_${cover.code}_value`} value={selectedCover?.value ?? cover.default} />
            {canUseChoiceButtons ? (
                <SwitchChoiceButtonInput
                    key={`${cover.code}-${selectedCover?.enabled}`}
                    name={`cover_${cover.code}_choice`}
                    title={t(determineCoverageName(cover.code))}
                    additionalContent={additionalContent}
                    switchName={`cover_${cover.code}_switch`}
                    switchDefaultChecked={selectedCover?.enabled ?? false}
                    switchDisabled={isSwitchDisabled}
                    options={options.map(option => ({
                        label: formatEuroValue(option, tenant, true),
                        value: String(option),
                    }))}
                    value={String(selectedCover?.value ?? cover.default)}
                    defaultValue={String(cover.default)}
                    disabled={!selectedCover?.enabled}
                    onSwitchChange={checked => onSwitchChange(cover.code, checked)}
                    onChange={value => onValueChange(cover.code, Number(value))}
                    footerContent={footerContent}
                />
            ) : (
                <RangeSlider
                    key={`${cover.code}-${selectedCover?.enabled}`}
                    name={`cover_${cover.code}_slider`}
                    title={t(determineCoverageName(cover.code))}
                    additionalContent={additionalContent}
                    headerActive
                    switchName={`cover_${cover.code}_switch`}
                    switchDefaultChecked={selectedCover?.enabled ?? false}
                    switchDisabled={isSwitchDisabled}
                    value={selectedCover?.value ?? cover.default}
                    defaultValue={cover.default}
                    min={min}
                    max={max}
                    step={step}
                    unit={RangeSliderUnit.Euro}
                    disabled={!selectedCover?.enabled}
                    onSwitchChange={checked => onSwitchChange(cover.code, checked)}
                    onChange={value => onValueChange(cover.code, value)}
                    footerContent={footerContent}
                />
            )}
        </div>
    );
}
